import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, Shield } from 'lucide-react';
import { companyInfo } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Contact', path: '/contact' },
    { name: 'Blog', path: '/blog' },
  ];

  const services = [
    { name: 'Armed Security', path: '/services/armed-security' },
    { name: 'Unarmed Security', path: '/services/unarmed-security' },
    { name: 'Fire Watch', path: '/services/fire-watch' },
    { name: 'Event Security', path: '/services/event-security' },
    { name: 'Commercial Security', path: '/services/commercial-security' },
  ];

  return (
    <footer className="bg-slate-950">
      {/* CTA Banner */}
      <div className="bg-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold text-white">
                {companyInfo.name} are always on guard for your family, business or event.
              </h3>
              <p className="text-white/80">Call now to inquire about our security guard services.</p>
            </div>
            <a 
              href={`tel:${companyInfo.phone.replace(/[^0-9]/g, '')}`}
              className="inline-flex items-center gap-2 bg-white text-orange-500 px-8 py-4 rounded-lg font-bold text-xl hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              <Phone className="w-6 h-6" />
              {companyInfo.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">{companyInfo.name}</h2>
              </div>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {companyInfo.name} is one of the top security companies in the United States. 
              With over 600 offices nationally, we offer a wide variety of security services.
            </p>
            <div className="flex gap-4">
              {[Facebook, Twitter, Instagram, Linkedin].map((Icon, index) => (
                <a 
                  key={index}
                  href="#"
                  className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center text-gray-400 hover:text-orange-500 hover:bg-slate-700 transition-all"
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-orange-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link 
                    to={service.path}
                    className="text-gray-400 hover:text-orange-400 transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`tel:${companyInfo.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-center gap-3 text-gray-400 hover:text-orange-400 transition-colors"
                >
                  <Phone className="w-5 h-5 text-orange-500" />
                  {companyInfo.phone}
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 text-gray-400 hover:text-orange-400 transition-colors"
                >
                  <Mail className="w-5 h-5 text-orange-500" />
                  {companyInfo.email}
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-gray-400">
                  <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span>600+ Offices Nationwide<br />Available in all 50 States</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm text-center md:text-left">
              © {currentYear} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-gray-500 hover:text-gray-400 text-sm">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-500 hover:text-gray-400 text-sm">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;